import React, { useState, useRef, useEffect, useMemo } from "react";
import { books, Category } from "../data/books";
import BookCard from "./BookCard";
import BookPreviewModal from "./BookPreviewModal";
import styles from "./Catalog.module.css";

import "../i18n";
import { useTranslation } from 'react-i18next';

const INITIAL_COUNT = 6;

const Catalog: React.FC = () => {
  const { t } = useTranslation();
  const tabs = [
    { label: t('catalog.tabs.all'), value: 'all' as const },
    { label: t('catalog.tabs.boys'), value: 'boys' as const },
    { label: t('catalog.tabs.girls'), value: 'girls' as const },
    { label: t('catalog.tabs.two'), value: 'two' as const },
  ];
  const [activeTab, setActiveTab] = useState<Category | "all">("all");
  const [isExpanded, setIsExpanded] = useState(false);
  const [displayAll, setDisplayAll] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);
  const [selectedBookId, setSelectedBookId] = useState<number | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageDirection, setPageDirection] = useState<"next" | "prev">("next");

  const filtered =
    activeTab === "all"
      ? books
      : activeTab === "two"
        ? books.filter((b) => b.category === "two")
        : books.filter((b) => b.category === activeTab || b.category === "all");

  const visibleBooks = displayAll ? filtered : filtered.slice(0, INITIAL_COUNT);
  const hasMore = filtered.length > INITIAL_COUNT;
  const collapsedRef = useRef<number>(9999);
  const [maxHeight, setMaxHeight] = useState<number>(9999);

  useEffect(() => {
    if (!gridRef.current || isExpanded) return;

    const cards = gridRef.current.querySelectorAll<HTMLElement>(
      `.${styles.cardWrapper}`,
    );
    if (cards.length === 0) return;

    const lastVisible = cards[Math.min(INITIAL_COUNT, cards.length) - 1];
    const gridTop = gridRef.current.getBoundingClientRect().top;
    const cardBottom = lastVisible.getBoundingClientRect().bottom;
    const collapsed = cardBottom - gridTop + 40;

    setMaxHeight(collapsed);
  }, [filtered.length, isExpanded]);
  useEffect(() => {
    if (!gridRef.current || isExpanded) return;
    const cards = gridRef.current.querySelectorAll<HTMLElement>(
      `.${styles.cardWrapper}`,
    );
    if (!cards.length) return;
    const last = cards[Math.min(INITIAL_COUNT, cards.length) - 1];
    const gridTop = gridRef.current.getBoundingClientRect().top;
    const bottom = last.getBoundingClientRect().bottom;
    const h = bottom - gridTop + 40;
    collapsedRef.current = h;
    setMaxHeight(h);
  }, [filtered.length, isExpanded]);

  const handleToggle = () => {
    if (!isExpanded) {
      setDisplayAll(true);
      setIsExpanded(true);
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (gridRef.current) setMaxHeight(gridRef.current.scrollHeight);
        }),
      );
    } else {
      setMaxHeight(collapsedRef.current);
      setIsExpanded(false);
      setTimeout(() => setDisplayAll(false), 560);
    }
  };

  const handleTabChange = (tab: Category | "all") => {
    setActiveTab(tab);
    setIsExpanded(false);
    setDisplayAll(false);
    setMaxHeight(collapsedRef.current);
  };

  const handlePrevPage = () => {
    setPageDirection("prev");
    setPageIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setPageDirection("next");
    setPageIndex((prev) => {
      if (selectedPages.length === 0) return prev;
      return Math.min(selectedPages.length - 1, prev + 1);
    });
  };

  const [pagesManifest, setPagesManifest] = useState<Record<string, number> | null>(null);

  useEffect(() => {
    fetch('/pages/manifest.json')
      .then((res) => res.json())
      .then((json) => setPagesManifest(json))
      .catch(() => setPagesManifest(null));
  }, []);

  const selectedBook = useMemo(
    () => books.find((book) => book.id === selectedBookId) ?? null,
    [selectedBookId],
  );

  const selectedPages = useMemo(() => {
    if (!selectedBook || !pagesManifest) return [];
    const dir = selectedBook.pagesDir;
    const count = pagesManifest[dir] ?? 0;
    const urls: string[] = [];
    for (let i = 0; i < count; i++) {
      urls.push(`/pages/${dir}/${i}.webp`);
    }
    return urls;
  }, [selectedBook, pagesManifest]);

  useEffect(() => {
    setPageIndex(0);
    setPageDirection("next");
  }, [selectedBookId]);

  return (
    <section className={styles.catalog} id="catalog">
      <div className={styles.inner}>
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>{t('catalog.title')}</h2>
            <p className={styles.subtitle}>{t('catalog.subtitle')}</p>
          </div>
          <div className={styles.tabs}>
            {tabs.map((tab) => (
              <button
                key={tab.value}
                className={`${styles.tab} ${activeTab === tab.value ? styles.tabActive : ""}`}
                onClick={() => handleTabChange(tab.value)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div
          ref={gridRef}
          className={`${styles.grid} ${isExpanded ? styles.gridFull : styles.gridFading}`}
          style={{ maxHeight: `${maxHeight}px` }}
        >
          {visibleBooks.map((book, i) => (
            <div
              key={book.id}
              className={styles.cardWrapper}
              style={{ animationDelay: `${(i % 6) * 60}ms` }}
            >
              <BookCard book={book} onOpen={() => setSelectedBookId(book.id)} />
            </div>
          ))}
        </div>

        {hasMore && (
          <div className={styles.expandBtn}>
            <button className={styles.btn} onClick={handleToggle}>
              {isExpanded ? t('catalog.hide') : t('catalog.show_more')}
            </button>
          </div>
        )}
      </div>

      <BookPreviewModal
        isOpen={Boolean(selectedBook)}
  title={selectedBook ? t(`books.${selectedBook.id}.title`, { defaultValue: selectedBook.title }) : ""}
        pages={selectedPages}
        pageIndex={pageIndex}
        direction={pageDirection}
        onClose={() => setSelectedBookId(null)}
        onPrev={handlePrevPage}
        onNext={handleNextPage}
      />
    </section>
  );
};

export default Catalog;
